import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { ApiConstants } from './_models/api-constants';
import { ServiceBase } from './_shared/services/service-base';
import { Backend } from './_models/backend';
import { CommonService } from './_shared/services/common.service';
import SystemOptionsForStudentDTO = Backend.SystemOptionsForStudentDTO;
import PasswordChangeRequest = Backend.PasswordChangeRequest;
import ChannelAuthInfoResponse = Backend.ChannelAuthInfoResponse;
import StudentStateDTO = Backend.StudentStateDTO;
import ClassroomStateForStudentDTO = Backend.ClassroomStateForStudentDTO;
import DebugLogType = Backend.DebugLogType;
import DebugLogRequest = Backend.DebugLogRequest;
import PollRequest = Backend.PollRequest;
import UpdateFormAnswerRequest = Backend.UpdateFormAnswerRequest;
import StudentProfileResponse = Backend.StudentProfileResponse;
import HomeworkSlimDTO = Backend.HomeworkSlimDTO;
import ViewHomeworkDTO = Backend.ViewHomeworkDTO;

const STUDENT_API = ApiConstants.API_STUDENT_ADDRESS_BASE;
const URL_STUDENT_PROFILE = STUDENT_API + ApiConstants.API_STUDENT_ADDRESS_PROFILE;
const URL_STUDENT_TOKEN_GENERATE = STUDENT_API + ApiConstants.API_STUDENT_ADDRESS_TOKEN_GENERATE;
const URL_STUDENT_CLASSROOM_STATES = STUDENT_API + ApiConstants.API_STUDENT_ADDRESS_CLASSROOM_STATES;
const URL_STUDENT_SYSTEM_OPTIONS = STUDENT_API + ApiConstants.API_STUDENT_ADDRESS_SYSTEM_OPTIONS;
const URL_STUDENT_TOKEN_REVOKE = STUDENT_API + ApiConstants.API_STUDENT_ADDRESS_TOKEN_REVOKE;
const URL_STUDENT_CLASS_LOGOUT = STUDENT_API + ApiConstants.API_STUDENT_ADDRESS_LOGOUT_CLASS;
const URL_STUDENT_POLL = STUDENT_API + ApiConstants.API_STUDENT_ADDRESS_POLL;
const URL_STUDENT_STUDENT_STATE = STUDENT_API + ApiConstants.API_STUDENT_ADDRESS_STUDENT_STATE;
const URL_STUDENT_RELOAD = STUDENT_API + ApiConstants.API_STUDENT_ADDRESS_RELOAD;
const URL_STUDENT_UPDATE_FORM = STUDENT_API + ApiConstants.API_STUDENT_ADDRESS_UPDATE_FORM;
const URL_STUDENT_SUBMIT_FORM = STUDENT_API + ApiConstants.API_STUDENT_ADDRESS_SUBMIT_FORM;
const URL_STUDENT_LIST_HOMEWORK = STUDENT_API + ApiConstants.API_STUDENT_ADDRESS_LIST_HOMEWORK;
const URL_STUDENT_GET_HOMEWORK = STUDENT_API + ApiConstants.API_STUDENT_ADDRESS_HOMEWORK;
const URL_STUDENT_UPDATE_HOMEWORK = STUDENT_API + ApiConstants.API_STUDENT_ADDRESS_UPDATE_HOMEWORK;
const URL_STUDENT_SUBMIT_HOMEWORK = STUDENT_API + ApiConstants.API_STUDENT_ADDRESS_SUBMIT_HOMEWORK;
const URL_STUDENT_PASSWORD_CHANGE = STUDENT_API + ApiConstants.API_STUDENT_ADDRESS_PASSWORD_CHANGE;
const URL_STUDENT_CHANGE_APP_IN_BACKGROUND = STUDENT_API + ApiConstants.API_STUDENT_ADDRESS_APP_IN_BACKGROUND_CHANGE;
const URL_STUDENT_DEBUG_LOG = STUDENT_API + ApiConstants.API_STUDENT_ADDRESS_DEBUG_LOG;

const URL_TEACHER_GET_RESOURCE = STUDENT_API + ApiConstants.API_STUDENT_GET_RESOURCE;

const FORMS_META_KEY = 'seos-forms-meta';
const FORMS_META_TIME_SPENT_KEY = 'forms-meta-time-spent-key';

const URL_STUDENT_ZOOM_AUTHORIZE_LINK = STUDENT_API + ApiConstants.API_STUDENT_ADDRESS_ZOOM_AUTHORIZE_LINK;


@Injectable({
  providedIn: 'root'
})
export class MainService implements ServiceBase {

  private images = new Map<number, any>();

  constructor(private http: HttpClient) {
  }

  public static replaceId(url: string, id: number): string {
    return url.replace('{id}', id.toString());
  }

  private static replaceClassroomId(url: string, classroomId: number): string {
    return url.replace('{classroomId}', classroomId.toString());
  }

  private static replaceHomeworkId(url: string, homeworkId: number): string {
    return url.replace('{homeworkId}', homeworkId.toString());
  }

  private static replaceInBackground(url: string, inBackground: boolean): string {
    return url.replace('{inBackground}', inBackground.toString());
  }

  getProfile(): Observable<StudentProfileResponse> {
    return this.http.get<StudentProfileResponse>(URL_STUDENT_PROFILE);
  }

  getClassroomStates(): Observable<ClassroomStateForStudentDTO[]> {
    return this.http.get<ClassroomStateForStudentDTO[]>(URL_STUDENT_CLASSROOM_STATES);
  }

  getSystemOptions(): Observable<SystemOptionsForStudentDTO> {
    return this.http.get<SystemOptionsForStudentDTO>(URL_STUDENT_SYSTEM_OPTIONS);
  }

  getStudentState(classroomId: number): Observable<StudentStateDTO> {
    const url = MainService.replaceClassroomId(URL_STUDENT_STUDENT_STATE, classroomId);
    return this.http.get<StudentStateDTO>(url);
  }

  getChannelToken(): Observable<ChannelAuthInfoResponse> {
    return this.http.get<ChannelAuthInfoResponse>(URL_STUDENT_TOKEN_GENERATE);
  }

  logoutFromClass(): Observable<void> {
    return this.http.get<void>(URL_STUDENT_CLASS_LOGOUT);
  }

  revokeToken(): Observable<void> {
    return this.http.get<void>(URL_STUDENT_TOKEN_REVOKE);
  }

  poll(pollRequest: PollRequest): Observable<StudentStateDTO> {
    return this.http.patch<StudentStateDTO>(URL_STUDENT_POLL, pollRequest, {context: CommonService.NO_SPINNER_CONTEXT});
  }

  reload(): Observable<StudentStateDTO> {
    return this.http.get<StudentStateDTO>(URL_STUDENT_RELOAD);
  }

  updateForm(answer: UpdateFormAnswerRequest): Observable<number> {
    return this.http.post<number>(URL_STUDENT_UPDATE_FORM, answer, {context: CommonService.NO_SPINNER_CONTEXT});
  }

  submitForm(answers: Record<string, any>): Observable<void> {
    return this.http.post<void>(URL_STUDENT_SUBMIT_FORM, answers);
  }

  listHomework(): Observable<HomeworkSlimDTO[]> {
    return this.http.get<HomeworkSlimDTO[]>(URL_STUDENT_LIST_HOMEWORK);
  }

  getHomework(homeworkId: number): Observable<ViewHomeworkDTO> {
    const url = MainService.replaceHomeworkId(URL_STUDENT_GET_HOMEWORK, homeworkId);
    return this.http.get<ViewHomeworkDTO>(url);
  }

  updateHomework(homeworkId: number, answer: UpdateFormAnswerRequest): Observable<number> {
    const url = MainService.replaceHomeworkId(URL_STUDENT_UPDATE_HOMEWORK, homeworkId);
    return this.http.put<number>(url, answer, {context: CommonService.NO_SPINNER_CONTEXT});
  }

  submitHomework(homeworkId: number, answers: Record<string, any>): Observable<void> {
    const url = MainService.replaceHomeworkId(URL_STUDENT_SUBMIT_HOMEWORK, homeworkId);
    return this.http.put<void>(url, answers);
  }

  changePassword(passwordChangeRequest: PasswordChangeRequest): Observable<boolean> {
    return this.http.put<boolean>(URL_STUDENT_PASSWORD_CHANGE, passwordChangeRequest);
  }

  getResource(resourceId: number): Observable<string | ArrayBuffer | null> {
    // This is a tiny blank image
    const blank = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

    const images = this.images;

    const image = this.images.get(resourceId);

    let subject: Subject<any> | null = null;
    if (image == null) {
      subject = new Subject<any>();
    }

    const observable = new Observable<string | ArrayBuffer | null>((observer) => {
      if (image != null) {
        if (!(image instanceof Observable)) {
          observer.next(image);
        }
      } else {
        const url = MainService.replaceId(URL_TEACHER_GET_RESOURCE, resourceId);

        observer.next(blank);

        // The next and error callbacks from the observer
        const {next, error} = observer;

        this.http.get(url, {responseType: 'blob', context: CommonService.NO_SPINNER_CONTEXT}).subscribe(response => {
          const reader = new FileReader();
          reader.readAsDataURL(response);
          reader.onloadend = function() {
            images.set(resourceId, new Observable<string | ArrayBuffer | null>((observer) => {
              observer.next(reader.result);
            }));
            subject?.next(reader.result);
            observer.next(reader.result);
          };
        });
      }

      return {
        unsubscribe() {
        }
      };
    });

    if (image == null) {
      const finalObservable = new Observable<string | ArrayBuffer | null>((observer) => {

        subject?.subscribe((data) => {
          console.log(data);
          observer.next(data);
        });

        return {
          unsubscribe() {
          }
        };
      });

      images.set(resourceId, finalObservable);
    } else {
      return image;
    }

    return observable;
  }

  changeAppInBackgroundStatus(inBackground: boolean): Observable<void> {
    const url = MainService.replaceInBackground(URL_STUDENT_CHANGE_APP_IN_BACKGROUND, inBackground);
    return this.http.patch<void>(url, null, {context: CommonService.NO_SPINNER_CONTEXT});
  }

  logMsg(message: string, type: DebugLogType = 'INFO'): Observable<void> {
    const log: DebugLogRequest = {message, type};
    return this.http.post<void>(URL_STUDENT_DEBUG_LOG, log, {context: CommonService.NO_SPINNER_CONTEXT});
  }

  getTimeSpent(key: string): number | null {
    const formMeta = this.getFormMeta();

    if (formMeta != null) {
      const timeSpent = formMeta.timeMap[FORMS_META_TIME_SPENT_KEY + '-' + key];

      if (timeSpent) {
        return timeSpent;
      }
    }

    return null;
  }

  setTimeSpent(key: string, timeSpent: number) {
    // console.log("Time spent: " + key + " - " + timeSpent);

    const formMeta = this.getFormMeta();

    if (formMeta != null) {
      formMeta.timeMap[FORMS_META_TIME_SPENT_KEY + '-' + key] = timeSpent;
      window.localStorage.setItem(FORMS_META_KEY, JSON.stringify(formMeta));
    }
  }

  clearTimeSpent(key: string) {
    // console.log("Clear time spent: " + key);

    const formMeta = this.getFormMeta();

    if (formMeta != null) {
      formMeta.timeMap.delete(FORMS_META_TIME_SPENT_KEY + '-' + key);
      window.localStorage.setItem(FORMS_META_KEY, JSON.stringify(formMeta));
    }
  }

  initFormMeta(contentId: number, endTime: number) {
    // console.log("Init meta: " + contentId + "  length: " + time + " mins");

    const now = new Date().getTime();
    let formMeta = this.getFormMeta();

    if (!formMeta || formMeta.contentId != contentId || formMeta.endTime < now) {
      const newFormMeta = {contentId: contentId, endTime: endTime, timeMap: new Map<string, number>()};
      this.setFormMeta(newFormMeta);
    }
  }

  clearFormMeta() {
    // console.log("Clear meta.");

    window.localStorage.removeItem(FORMS_META_KEY);
  }

  getFormMeta(): any {
    const formMeta = window.localStorage.getItem(FORMS_META_KEY);

    if (formMeta != null) {
      return JSON.parse(formMeta);
    }
    return null;
  }

  setFormMeta(formMeta: any): void {
    window.localStorage.setItem(FORMS_META_KEY, JSON.stringify(formMeta));
  }

  getZoomAuthorizeLink(): Observable<string> {
    return this.http.get(URL_STUDENT_ZOOM_AUTHORIZE_LINK, {responseType: 'text'});
  }

}
